<template>
  <div
    v-show="realVisible"
    v-if="realVisible || !destroy"
    :class="_wrapperClass"
    :style="_wrapperStyle"
  >
    <template v-if="withMask">
      <transition
        name="ten-fade-in"
        appear
      >
        <div
          v-show="visible"
          ref="mask"
          class="ten-modal__mask"
          @click="onMaskClick"
        />
      </transition>
    </template>
    <transition
      name="ten-flow-in"
      appear
    >
      <div
        v-bind="$attrs"
        v-show="visible"
        :class="_modalClass"
        :style="_modalStyle"
        ref="modal"
      >
        <div
          v-if="title && !hideHeader"
          class="ten-modal__header"
          :style="_handleMoveStyle"
          @mousedown="handeleMove"
        >
          <ten-icon
            v-if="type"
            :class="[
              'ten-modal__header-icon',
              { [`ten-modal__header-icon--${type}`]: type }
            ]"
            :icon="_titleIconType"
          />
          <div class="ten-modal__header-title">
            <slot name="title">
              {{ title }}
            </slot>
          </div>
          <ten-icon
            class="ten-modal__header-close"
            icon="clear"
            @click="onModalClose"
          />
        </div>
        <div class="ten-modal__content">
          <!-- @slot content 内容 -->
          <slot>
            <template v-if="typeof content === 'string'">
              {{ content }}
            </template>
            <template v-if="content && typeof content === 'function'">
              <render-cell :render="content" />
            </template>
            <template v-if="content && typeof content === 'object'">
              <vnodes :vnodes="content" />
            </template>
          </slot>
        </div>
        <div
          v-if="!hideFooter"
          class="ten-modal__footer"
        >
          <!-- @slot footer 内容 -->
          <slot name="footer">
            <ten-button
              v-if="!cancelHidden"
              class="ten-modal__footer-btn ten-modal__footer-btn--cancel"
              v-bind="cancelBtnProps"
              @click="onModalCancel"
            >
              {{ cancelText || t('cancel') }}
            </ten-button>
            <ten-button
              class="ten-modal__footer-btn ten-modal__footer-btn--confirm"
              type="primary"
              v-bind="confirmBtnProps"
              @click="onModalConfirm"
            >
              {{ confirmText || t('confirm') }}
            </ten-button>
          </slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Button from '../button';
import Icon from '../icon';
import RenderCell from '../render-cell';
import Vnodes from '../vnodes';
import Emitter from '../scripts/mixins/emitter';
import {
  validateBeforeAction,
  removeUndefinedAttr,
} from '../scripts/utils/helper';
import domHelper from '../scripts/utils/dom-helper';
import { localMixin } from '../config';

const name = 'ten-modal';

const ICON_TYPE_MAP = {
  error: 'circle_clear',
  warning: 'circle_error',
  info: 'circle_info',
  success: 'circle_tick',
};

export default {
  name,
  components: {
    TenButton: Button,
    TenIcon: Icon,
    RenderCell,
    Vnodes,
  },
  mixins: [Emitter, localMixin],
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    /**
     * 是否显示Modal
     */
    value: Boolean,
    /**
     * 标题
     */
    title: String,
    /**
     * 对话框宽度
     */
    width: [String, Number],
    /**
     * 对话框距离顶部的距离
     */
    top: [String, Number],
    /**
     * 是否垂直居中显示对话框
     */
    center: Boolean,
    /**
     * 类型 可选 success | error | warning | info
     */
    type: {
      type: String,
      validator(v) {
        return ['success', 'error', 'warning', 'info'].indexOf(v) > -1;
      },
    },
    /**
     * modal 内容，默认渲染 modal 的 default slot
     */
    content: {
      type: [String, Function, Object],
    },
    /**
     * 确认按钮文字
     */
    confirmText: String,
    /**
     * 取消按钮文字
     */
    cancelText: String,
    /**
     * 确认按钮 props
     */
    confirmBtnProps: Object,
    /**
     * 取消按钮 props
     */
    cancelBtnProps: Object,
    /**
     * 自定义icon
     * @member icon图标列表
     */
    iconType: String,
    /**
     * 是否隐藏取消按钮
     */
    cancelHidden: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否显示遮罩层
     */
    withMask: {
      type: Boolean,
      default: true,
    },
    /**
     * 点击遮罩层是否关闭modal
     */
    maskClosable: {
      type: Boolean,
      default: true,
    },
    /**
     * 开启键盘监听 ESC 退出事件
     */
    escClosable: {
      type: Boolean,
      default: false
    },
    /**
     * 关闭modal是否销毁
     */
    destroy: {
      type: Boolean,
      default: true,
    },
    /**
     * 确认按钮点击回调，返回 true 或 false 决定是否继续执行 confirm 操作并关闭 modal，也支持返回 promise 异步处理
     */
    beforeConfirm: Function,
    /**
     * 取消按钮点击回调，返回 true 或 false 决定是否继续执行 cancel 操作并关闭 modal，也支持返回 promise 异步处理
     */
    beforeCancel: Function,
    /**
     * 关闭按钮或 mask 点击触发的关闭回调，返回 true 或 false 决定是否关闭 modal，也支持返回 promise 异步处理
     */
    beforeClose: Function,
    /**
     * 不显示头部
     */
    hideHeader: Boolean,
    /**
     * 不显示尾部
     */
    hideFooter: Boolean,
    /**
     * 点击确认或者取消是否关闭 modal
     */
    closeAfterAction: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否开启拖拽控制移动
     */
    draggable: {
      type: Boolean,
      default: false,
    },
    /**
     * 自定义 modal 容器 class
     */
    wrapperClass: [String, Array, Object],
  },
  data() {
    return {
      visible: this.value,
      realVisible: this.value,
    };
  },
  computed: {
    _wrapperClass() {
      return ['ten-modal__wrapper', this.wrapperClass || ''];
    },
    _modalClass() {
      return [
        'ten-modal',
        {
          'ten-modal--center': this.center,
        },
      ];
    },
    _modalStyle() {
      const width = domHelper.getCssWidth(this.width);
      const style = { width };

      if (!this.center) {
        style.top = domHelper.getCssWidth(this.top);
      }

      return !this.withMask
        ? { ...removeUndefinedAttr(style), 'pointer-events': 'initial' }
        : removeUndefinedAttr(style);
    },
    _titleIconType() {
      return this.iconType || ICON_TYPE_MAP[this.type];
    },
    _handleMoveStyle() {
      return this.draggable ? { cursor: 'move' } : '';
    },
    _wrapperStyle() {
      return !this.withMask ? { 'pointer-events': 'none' } : '';
    },
  },
  watch: {
    value(value) {
      this.setVisible(value, true);
    },
  },
  mounted() {
    this.aniT = 0;
    if (this.visible) {
      this.appendToBody();
    }

    this.escClosable && document.addEventListener('keydown', this.onEscClose);
  },
  destroyed() {
    try {
      this.escClosable && document.removeEventListener('keydown', this.onEscClose);
      this.$el && document.body.removeChild(this.$el);
    } catch (e) {}
  },
  methods: {
    onEscClose(e) {
      if (e.keyCode === 27 && !e.isComposing) {
        this.setVisible(false);
      }
    },
    appendToBody() {
      if (this.$el && this.$el.parentNode !== document.body) {
        document.body.appendChild(this.$el);
      }
    },
    setBodyCalcSize(clear) {
      const bodyIsScroll =        document.body.scrollHeight
          > (window.innerHeight || document.documentElement.clientHeight)
        && window.innerWidth > document.body.offsetWidth;
      if (!bodyIsScroll) {
        return;
      }
      if (clear) {
        document.body.style.position = '';
        document.body.style.width = '';
        return;
      }
      const calcSize = domHelper.getBrowserScrollSize();

      if (calcSize) {
        document.body.style.position = 'relative';
        document.body.style.width = `calc(100% - ${calcSize}px)`;
      }
    },
    onModalConfirm() {
      validateBeforeAction(this.beforeConfirm, () => {
        /**
         * @event confirm
         * 点击确认回调函数
         */
        this.$emit('confirm');

        if (this.closeAfterAction) {
          this.close();
        }
      });
    },
    onModalCancel() {
      validateBeforeAction(this.beforeCancel, () => {
        /**
         * @event cancel
         * 点击取消回调函数
         */
        this.$emit('cancel');

        if (this.closeAfterAction) {
          this.close();
        }
      });
    },
    onModalClose() {
      validateBeforeAction(this.beforeClose, () => {
        this.close();
      });
    },
    show() {
      this.setVisible(true);
    },
    close() {
      this.setVisible(false);
    },
    onMaskClick() {
      this.maskClosable && this.onModalClose();
    },
    setVisible(value, initiative) {
      let isChanged = false;
      if (!value && this.visible) {
        /**
         * 关闭回调函数
         */
        this.$emit('close');
        isChanged = true;
        this.visible = value;
        clearTimeout(this.aniT);
        this.aniT = setTimeout(() => {
          document.body.style.overflow = '';
          this.setBodyCalcSize(true);
          /**
           * 关闭动画结束回调函数
           */
          this.$emit('closed');
          this.realVisible = false;
        }, 300);
      } else if (value && !this.visible) {
        isChanged = true;
        this.visible = value;
        this.realVisible = value;
        this.appendToBody();
        this.setBodyCalcSize();
        document.body.style.overflow = 'hidden';
        /**
         * 显示回调函数
         */
        this.$emit('open');
        clearTimeout(this.aniT);
        this.aniT = setTimeout(() => {
          /**
           * 显示动画结束回调函数
           */
          this.$emit('opened');
        }, 300);
      }
      /**
       * 状态改变回调函数
       */
      if (isChanged && !initiative) {
        this.$emit('change', value);
      }
      this.broadcastAll('do-layout');
    },
    handeleMove(event) {
      // ps： 整个坐标轴是 T 字形的，建议后期可以更改为了浏览器的坐标轴
      if (!this.draggable) return;
      // 初始化 modal 的位置
      const modalDom = this.$refs.modal;

      let styleLeft = modalDom.style.left || '0';
      styleLeft = parseInt(styleLeft.replace('px', ''), 10);

      const { top, left, right } = modalDom.getBoundingClientRect();
      modalDom.style.top = `${top}px`;
      modalDom.style.transform = 'none';

      // 获取盒子的数据，方便限制移动
      const { clientHeight } = document.body;
      const { offsetHeight: modalHeight, offsetWidth: modalWidth } = modalDom;
      const maxMoveHeight = clientHeight - modalHeight;

      // 鼠标初始位置
      const { pageX: startX, pageY: startY } = event;
      // 监听位置的变化，并计算长度
      let stop = true;
      const mousemoveFn = ({ pageX, pageY }) => {
        if (!stop) return false;
        // 控制x 轴方向的运动
        let distanceX = styleLeft + pageX - startX;
        if (distanceX > 0) {
          if (distanceX >= right - modalWidth) distanceX = right - modalWidth;
        } else {
          if (distanceX <= -left) distanceX = -left;
        }
        modalDom.style.left = `${distanceX}px`;

        // 控制 y 轴方向的运动
        let distanceY = top + pageY - startY;
        if (distanceY > 0) {
          if (distanceY >= maxMoveHeight) distanceY = maxMoveHeight;
        } else {
          distanceY = 0;
        }
        modalDom.style.top = `${distanceY}px`;
      };
      const mouseupFn = () => {
        stop = false;
        document.removeEventListener('mousemove', mousemoveFn);
        document.removeEventListener('mouseup', mouseupFn);
      };

      document.addEventListener('mousemove', mousemoveFn);
      document.addEventListener('mouseup', mouseupFn);
    },
  },
};
</script>
